// src/lib/nlp-processor.ts
import { parse, addDays, addWeeks, startOfToday, isValid } from 'date-fns';

interface ParsedQuery {
  destination?: string;
  origin?: string;
  departureDate?: Date;
  returnDate?: Date;
  adults?: number;
  children?: number;
  /** Child ages 1–17 when present in query (e.g. "1 kid 17yo", "2 kids 5 and 12"). Required by RateHawk when children > 0. */
  childrenAges?: number[];
}

function parseDateString(dateStr: string): Date | undefined {
  const normalized = dateStr.toLowerCase()
    .replace(/(st|nd|rd|th)/, '')
    .trim();
  
  const formats = [
    'MMMM d yyyy',  // December 25 2024
    'MMMM d',       // December 25
    'd MMMM yyyy',  // 25 December 2024
    'd MMMM',       // 25 December
    'MMM d yyyy',   // Dec 25 2024
    'MMM d',        // Dec 25
    'd MMM yyyy',   // 25 Dec 2024
    'd MMM'         // 25 Dec
  ];
  
  for (const format of formats) {
    try {
      const date = parse(normalized, format, new Date());
      if (isValid(date)) {
        if (!normalized.includes(date.getFullYear().toString())) {
          const today = new Date();
          if (date < today) {
            date.setFullYear(today.getFullYear() + 1);
          } else {
            date.setFullYear(today.getFullYear());
          }
        }
        return date;
      }
    } catch {
      continue;
    }
  }
  return undefined;
}

function parseRelativeDate(text: string): Date | undefined {
  const today = startOfToday();
  const normalized = text.toLowerCase().trim();

  if (normalized === 'tomorrow') return addDays(today, 1);
  if (normalized === 'today') return today;
  if (normalized === 'next week') return addWeeks(today, 1);

  if (normalized.includes('next') || normalized.includes('this')) {
    const dayName = normalized.split(' ')[1];
    try {
      const targetDay = parse(dayName, 'EEEE', today);
      if (isValid(targetDay)) {
        return addDays(targetDay, normalized.includes('next') ? 7 : 0);
      }
    } catch {
      return undefined;
    }
  }

  return undefined;
}

export function parseNaturalQuery(query: string): ParsedQuery {
  const result: ParsedQuery = {};
  const cleanQuery = query.toLowerCase().trim();

  if (cleanQuery.length < 10) {
    return result;
  }

  // Add relative date parsing
  const relativeDateWords = ['tomorrow', 'today', 'next week', 'next monday', 'this friday'];
  for (const word of relativeDateWords) {
    if (cleanQuery.includes(word)) {
      const date = parseRelativeDate(word);
      if (date) {
        result.departureDate = date;
        break;
      }
    }
  }

  // Destination parsing
  const destinationRegex = /(?:i\s*(?:want|plan|need|would like|am thinking|intend)\s*(?:to)?\s*)?(?:go|fly|travel|head|leave|move|relocate|take a trip|book a trip|planning a trip|journey|visit)\s*(?:to|towards)?\s+([a-zA-Z\s,]+?)(?:\s+(?:from|on|with|for|in|by)|[.!?]|$)/i;
  const destinationMatch = cleanQuery.match(destinationRegex);
  if (destinationMatch?.[1]) {
    result.destination = destinationMatch[1].trim();
  }

  // Origin parsing
  const originRegex = /(?:i\s*(?:am|will be|was)?\s*)?(?:from|leaving from|leaving|departing from|departing|coming from|originating in|based in|starting (?:my\s*)?trip from|traveling from)\s+([a-zA-Z\s,]+?)(?:\s+(?:to|on|for|with|in|by)|[.!?]|$)/i;
  const originMatch = cleanQuery.match(originRegex);
  if (originMatch?.[1]) {
    result.origin = originMatch[1].trim();
  }

  // Date and duration patterns
  const datePatterns = [
    // Explicit date + duration: "on July 10 for 20 days"
    {
      pattern: /on\s+((?:\d{1,2}(?:st|nd|rd|th)?\s+)?(?:January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\.?\s+\d{1,2}(?:st|nd|rd|th)?(?:\s+\d{4})?)\s+for\s+(\d+)\s*days?/i,
      handler: (match: RegExpMatchArray) => {
        const startDate = parseDateString(match[1]);
        const duration = parseInt(match[2]);
        if (startDate && !isNaN(duration)) {
          return {
            departureDate: startDate,
            returnDate: addDays(startDate, duration)
          };
        }
        return null;
      }
    },
    // Simple date: "on July 10"
    {
      pattern: /on\s+((?:\d{1,2}(?:st|nd|rd|th)?\s+)?(?:January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\.?\s+\d{1,2}(?:st|nd|rd|th)?(?:\s+\d{4})?)/i,
      handler: (match: RegExpMatchArray): { departureDate: Date; returnDate?: Date } | null => {
        const date = parseDateString(match[1]);
        return date ? { departureDate: date } : null;
      }
    },
    // Duration from today: "for 20 days" or "X weeks"
    {
      pattern: /(?:for\s+)?(\d+)\s*(?:days?|weeks?)/i,
      handler: (match: RegExpMatchArray) => {
        const value = parseInt(match[1]);
        if (!isNaN(value)) {
          const today = startOfToday();
          // Start trip in 2 weeks from today by default
          const departureDate = addDays(today, 14);
          // Determine duration in days
          const durationInDays = match[0].toLowerCase().includes('week') ? value * 7 : value;
          return {
            departureDate: departureDate,
            returnDate: addDays(departureDate, durationInDays),
            duration: durationInDays
          };
        }
        return null;
      }
    }
  ];

  // Try each date pattern
  for (const { pattern, handler } of datePatterns) {
    const match = cleanQuery.match(pattern);
    if (match) {
      const dates = handler(match);
      if (dates) {
        if (dates.departureDate) result.departureDate = dates.departureDate;
        if (dates.returnDate) result.returnDate = dates.returnDate;
        break;
      }
    }
  }

  // Adults parsing
  const adultsPatterns = [
    /(\d+)\s*adults?/i,
    /for\s+(\d+)\s*(?:person|people|passenger|passengers)(?:\s|$)/i,
    /(\d+)\s*(?:person|people|passenger|passengers)(?:\s|$)/i,
    /(?:with|including|total of|group of)\s+(\d+)\s*(?:adult|adults|person|people|passenger|passengers)(?:\s|$)/i,
    /(?:traveling|flying|going)\s+with\s+(\d+)\s*(?:adult|adults|person|people|passenger|passengers)(?:\s|$)/i,
    /(?:party of|group size|number of travelers|travelers)\s*:?\s*(\d+)(?:\s|$)/i,
    /(?:we are|we're|there are|there's)\s+(\d+)\s*(?:of us|adults|people|passengers)(?:\s|$)/i,
    // Family member patterns
    /(?:with|including|and)\s+(?:my|the)\s+(?:wife|husband|spouse|partner)(?:\s|$)/i,
    /(?:with|including|and)\s+(?:my|the)\s+(?:mother|father|mom|dad|parent|parents)(?:\s|$)/i,
    /(?:with|including|and)\s+(?:my|the)\s+(?:sister|brother|sibling|siblings)(?:\s|$)/i,
    /(?:with|including|and)\s+(?:my|the)\s+(?:grandmother|grandfather|grandma|grandpa|grandparent|grandparents)(?:\s|$)/i,
    /(?:with|including|and)\s+(?:my|the)\s+(?:aunt|uncle|cousin|cousins)(?:\s|$)/i,
    /(?:with|including|and)\s+(?:my|the)\s+(?:in-laws|mother-in-law|father-in-law|sister-in-law|brother-in-law)(?:\s|$)/i,
    /(?:family|families)\s+(?:of|with)\s+(\d+)\s*(?:adults?|people|members)(?:\s|$)/i,
    /(?:couple|couples)\s+(?:of|with)\s+(\d+)\s*(?:adults?|people|members)(?:\s|$)/i,
    // Natural language family patterns
    /(?:i|we)\s+(?:have|got|own)\s+(?:a|one)\s+(?:wife|husband|spouse|partner)(?:\s|$)/i,
    /(?:i|we)\s+(?:have|got|own)\s+(?:a|one)\s+(?:mother|father|mom|dad|parent)(?:\s|$)/i,
    /(?:i|we)\s+(?:have|got|own)\s+(?:a|one)\s+(?:sister|brother|sibling)(?:\s|$)/i,
    /(?:i|we)\s+(?:have|got|own)\s+(?:a|one)\s+(?:grandmother|grandfather|grandma|grandpa|grandparent)(?:\s|$)/i,
    /(?:i|we)\s+(?:have|got|own)\s+(?:a|one)\s+(?:aunt|uncle|cousin)(?:\s|$)/i,
    /(?:i|we)\s+(?:have|got|own)\s+(?:a|one)\s+(?:in-law|mother-in-law|father-in-law|sister-in-law|brother-in-law)(?:\s|$)/i,
    /(?:i|we)\s+(?:live|reside|stay)\s+(?:with|together with)\s+(?:my|our|the)\s+(?:family|families)(?:\s|$)/i,
    /(?:i|we)\s+(?:am|are)\s+(?:a|part of)\s+(?:family|families)\s+(?:of|with)\s+(\d+)\s*(?:members|people|adults?)(?:\s|$)/i
  ];

  for (const pattern of adultsPatterns) {
    const adultsMatch = cleanQuery.match(pattern);
    if (adultsMatch) {
      // For family member patterns without numbers, increment by 1
      if (pattern.toString().includes('wife|husband|spouse|partner|mother|father|sister|brother|grandmother|grandfather|aunt|uncle|cousin|in-laws|family|families')) {
        result.adults = (result.adults || 0) + 1;
        continue;
      }
      
      // For patterns with numbers
      if (adultsMatch[1]) {
        const count = parseInt(adultsMatch[1]);
        if (count > 0 && count < 10) {
          result.adults = count;
          break;
        }
      }
    }
  }

  // Children parsing
  const childrenPatterns = [
    /(\d+)\s*(?:kids?|children|child)/i,
    /with\s+(\d+)\s*(?:kids?|children|child)/i,
    /(?:including|plus|and)\s+(\d+)\s*(?:kids?|children|child)(?:\s|$)/i,
    /(?:traveling|flying|going)\s+with\s+(\d+)\s*(?:kids?|children|child)(?:\s|$)/i,
    /(?:family of|family with)\s+(\d+)\s*(?:kids?|children|child)(?:\s|$)/i,
    /(?:we have|we've got|there are|there's)\s+(\d+)\s*(?:kids?|children|child)(?:\s|$)/i,
    /(?:minor|minors|young|younger)\s+(?:travelers?|passengers?)\s*:?\s*(\d+)(?:\s|$)/i,
    // Natural language children patterns
    /(?:i|we)\s+(?:have|got|own)\s+(\d+)\s+(?:kids?|children|child)(?:\s|$)/i,
    /(?:i|we)\s+(?:have|got|own)\s+(?:a|one)\s+(?:kid|child|baby|infant|toddler)(?:\s|$)/i,
    /(?:i|we)\s+(?:have|got|own)\s+(?:(\d+)\s+)?(?:kids?|children|child)(?:\s|$)/i,
    /(?:one|some|(\d+))\s+(?:of|among|which|whom)\s+(?:is|are)\s+(?:a|an)\s+(?:baby|infant|toddler|child|kid)(?:\s|$)/i,
    /(?:with|including)\s+(?:a|one|(\d+))\s+(?:baby|infant|toddler|child|kid)(?:\s|$)/i
  ];

  for (const pattern of childrenPatterns) {
    const childrenMatch = cleanQuery.match(pattern);
    if (childrenMatch) {
      // For child patterns without numbers, increment by 1
      if (pattern.toString().includes('infant|toddler|child|kid') && !childrenMatch[1]) {
        result.children = (result.children || 0) + 1;
        continue;
      }
      
      // For patterns with numbers
      if (childrenMatch[1]) {
        const count = parseInt(childrenMatch[1]);
        if (count >= 0 && count < 10) {
          result.children = count;
          break;
        }
      }
    }
  }

  // Child ages (1–17). RateHawk requires ages when children > 0. Parse "1 kid 17yo", "2 kids 5 and 12".
  const collectedAges: number[] = [];
  // "1 kid 17yo" / "child 17 years old" / "1 kid age 17"
  const afterKid = /\b(?:kid|child|kids?|children)\s+(?:of\s+)?(?:age\s+)?(\d{1,2})\s*(?:yo|y\.?o\.?|years?\s*old)?/gi;
  let m;
  while ((m = afterKid.exec(cleanQuery)) !== null) {
    const age = parseInt(m[1], 10);
    if (age >= 1 && age <= 17) collectedAges.push(age);
  }
  // "2 kids 5 and 12" / "kids 5 and 12"
  const kidsAndAges = /\b(?:kids?|children|child)\s+(\d{1,2})(?:\s+and\s+(\d{1,2}))?(?:\s+and\s+(\d{1,2}))?/gi;
  while ((m = kidsAndAges.exec(cleanQuery)) !== null) {
    for (let i = 1; i < m.length; i++) {
      if (m[i]) {
        const age = parseInt(m[i], 10);
        if (age >= 1 && age <= 17) collectedAges.push(age);
      }
    }
  }
  // "17yo" or "17 yo" near end of query (when we already have children)
  if ((result.children ?? 0) > 0 && collectedAges.length === 0) {
    const yoMatch = cleanQuery.match(/(\d{1,2})\s*(?:yo|y\.?o\.?|years?\s*old)/gi);
    if (yoMatch) {
      for (const part of yoMatch) {
        const num = parseInt(part.replace(/\D/g, ''), 10);
        if (num >= 1 && num <= 17) collectedAges.push(num);
      }
    }
  }
  if (collectedAges.length > 0) {
    const childCount = result.children ?? collectedAges.length;
    const uniqueAges = Array.from(new Set(collectedAges));
    result.childrenAges = uniqueAges.slice(0, childCount);
    if (result.children === undefined) result.children = result.childrenAges.length;
  }

  return result;
}
